/**
 * The admin allowlist (spec §5). Admin is flat and global: everyone here can
 * do everything, including editing this page, so the only guards are the two
 * that keep the allowlist from being emptied.
 */
import { useState } from "react";
import { api, apiErrorMessage } from "../../lib/api";
import { useAdminAdmins, useAdminAllowlistMutation } from "../../lib/adminData";
import { useSession } from "../../lib/me";
import type { AdminUser } from "../../lib/types";
import {
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  PageHeader,
  SectionCard,
  Skeleton,
  stagger,
  TrashIcon,
  Workbench,
} from "../../components/ui";

/** "Jul 26, 2026" from an ISO timestamp; an em dash for rows that predate it. */
function addedOn(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * How a row names its admin, everywhere it has to: the line on the card, the
 * button's accessible name, and the confirm() that asks before revoking. Those
 * three have to agree on who is losing access, so they share one derivation.
 *
 * Email leads because that is the handle admin is granted by — the form below
 * asks for an email, not a name. The uid only surfaces for an account that
 * never finished the signup wizard, and showing it beats showing nothing.
 */
const adminLabel = (admin: AdminUser): string => admin.email ?? admin.name ?? admin.uid;

function AdminRow({ admin, isSelf }: { admin: AdminUser; isSelf: boolean }) {
  const [error, setError] = useState<string>();
  const remove = useAdminAllowlistMutation(() => api.del(`/admin/admins/${admin.uid}`));
  const label = adminLabel(admin);
  const added = `Added ${addedOn(admin.addedAt)}`;

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-medium text-navy-950">
            {label}
            {isSelf ? <span className="ml-2 text-xs font-normal text-slate-400">you</span> : null}
          </p>
          {/* The name goes underneath, unless it is already the line above. */}
          <p className="truncate text-sm text-slate-500">
            {admin.name && admin.name !== label ? `${admin.name} · ${added}` : added}
          </p>
        </div>

        <button
          type="button"
          disabled={isSelf || remove.isPending}
          title={isSelf ? "You cannot remove your own admin access" : "Remove admin"}
          aria-label={`Remove ${label}`}
          onClick={() => {
            if (!confirm(`Remove admin access for ${label}?`)) return;
            remove.mutate(undefined, {
              onError: (e) => setError(apiErrorMessage(e, "Could not remove this admin.")),
            });
          }}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>

      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
    </Card>
  );
}

export function AdminAdmins() {
  const adminsQ = useAdminAdmins();
  const session = useSession();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string>();

  const add = useAdminAllowlistMutation(() =>
    api.post("/admin/admins", { email: email.trim().toLowerCase() }),
  );

  const admins = adminsQ.data?.admins ?? [];

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Admin"
        title="Admins"
        subtitle="Anyone here can manage every tournament, venue, and registration."
      />

      <Workbench
        aside={
          <SectionCard title="Add an admin" className="animate-rise" style={stagger(2)}>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (!email.trim()) return;
                add.mutate(undefined, {
                  onSuccess: () => {
                    setEmail("");
                    setError(undefined);
                  },
                  // The 404 and 409 both mean "fix the address in the box above",
                  // so they belong beside the field rather than in a toast.
                  onError: (err) =>
                    setError(
                      apiErrorMessage(
                        err,
                        "No account uses that email yet. Ask them to sign up first, then add them.",
                      ),
                    ),
                });
              }}
            >
              <Field
                label="Email"
                hint="They must already have an AlphaTournaments account — admin is granted to an existing sign-in."
              >
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="coach@example.com"
                  maxLength={320}
                  required
                />
              </Field>

              {error ? <p className="text-sm text-red-600">{error}</p> : null}

              <Button type="submit" disabled={!email.trim() || add.isPending}>
                {add.isPending ? "Adding…" : "Add admin"}
              </Button>
            </form>
          </SectionCard>
        }
      >
        <div className="animate-rise space-y-3" style={stagger(1)}>
          {adminsQ.isLoading ? (
            <>
              <Skeleton className="h-20 rounded-2xl" />
              <Skeleton className="h-20 rounded-2xl" />
            </>
          ) : adminsQ.isError ? (
            // Before the empty case, and in red rather than the dashed
            // placeholder that case uses: a failed request has no admins either,
            // and falling through would say nobody holds admin access on the one
            // page whose subject is who does.
            <p className="text-sm text-red-600">
              Couldn&rsquo;t load the admin list. Check your connection and try again.
            </p>
          ) : admins.length === 0 ? (
            // "Add an admin" sits below this on a phone and beside it on a wide
            // screen, so the hint names the form rather than pointing at it.
            <EmptyState title="No admins yet" hint="Use “Add an admin” to grant access by email." />
          ) : (
            admins.map((a) => (
              <AdminRow key={a.uid} admin={a} isSelf={a.uid === session.data?.uid} />
            ))
          )}
        </div>
      </Workbench>
    </div>
  );
}
